import {
  GoogleAuthProvider,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export function mapGoogleAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code || '';
  if (code === 'auth/popup-closed-by-user') return 'Sign-in was cancelled.';
  if (code === 'auth/popup-blocked') {
    return 'The popup was blocked. Please allow popups for this site or use Safari/Chrome directly.';
  }
  if (code === 'auth/cancelled-popup-request') return 'Sign-in was interrupted. Please try again in a moment.';
  if (code === 'auth/unauthorized-domain') {
    return (
      'This domain is not authorized in Firebase Auth.\n\n' +
      'Add web2pos.com under Firebase Console → Authentication → Settings → Authorized domains.'
    );
  }
  if (code === 'auth/operation-not-allowed') {
    return 'Google sign-in is disabled in Firebase. Enable Google under Authentication → Sign-in method.';
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return 'This email is already registered with a different sign-in method.';
  }
  if (code === 'auth/network-request-failed') {
    return 'Google sign-in failed due to a network error. Check your connection and try again.';
  }
  return `Google sign-in failed. (${code || 'unknown'})`;
}

/** Google popup 완료 후 Firestore users 등록 확인 */
export async function finishGoogleSignIn(user: User): Promise<{ ok: true } | { ok: false; error: string }> {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    await setDoc(
      userRef,
      {
        lastLoginAt: new Date(),
      },
      { merge: true },
    );
    return { ok: true };
  }

  if (user.email) {
    const email = user.email.trim().toLowerCase();
    const usersQuery = query(collection(db, 'users'), where('email', '==', email));
    const emailSnap = await getDocs(usersQuery);

    if (!emailSnap.empty) {
      const existingData = emailSnap.docs[0].data();
      await setDoc(userRef, {
        ...existingData,
        uid: user.uid,
        photoURL: user.photoURL || existingData.photoURL,
        lastLoginAt: new Date(),
        loginMethod: 'google',
      });
      return { ok: true };
    }

    // Business Info contacts do not have a users/{uid} document until the
    // signed-in session calls syncBusinessInfoMembership in App.tsx.
    const businessEmailMatches = await Promise.all([
      getDocs(query(collection(db, 'restaurants'), where('email', '==', email))),
      getDocs(query(collection(db, 'restaurants'), where('operatorEmail', '==', email))),
      getDocs(query(collection(db, 'restaurants'), where('accountantEmail', '==', email))),
    ]);
    if (businessEmailMatches.some((snapshot) => !snapshot.empty)) {
      return { ok: true };
    }
  }

  await signOut(auth);
  return { ok: false, error: 'This account is not registered. Please contact your administrator.' };
}

export async function consumeGoogleRedirectResult(): Promise<
  { handled: false } | { handled: true; ok: true } | { handled: true; ok: false; error: string }
> {
  const result = await getRedirectResult(auth);
  if (!result) return { handled: false };
  const finished = await finishGoogleSignIn(result.user);
  return finished.ok
    ? { handled: true, ok: true }
    : { handled: true, ok: false, error: finished.error };
}

function isMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isAppleMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export async function startGoogleSignIn(): Promise<
  | { mode: 'redirect'; ok: true }
  | { mode: 'popup'; ok: true }
  | { mode: 'popup'; ok: false; error: string }
> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  // Desktop popup avoids Firebase redirect state being lost at /__/auth/handler.
  // Mobile keeps redirect because popups are commonly blocked or unreliable there.
  if (!isMobileBrowser() || isAppleMobileBrowser()) {
    const result = await signInWithPopup(auth, provider);
    const finished = await finishGoogleSignIn(result.user);
    return finished.ok
      ? { mode: 'popup', ok: true }
      : { mode: 'popup', ok: false, error: finished.error };
  }

  await signInWithRedirect(auth, provider);
  return { mode: 'redirect', ok: true };
}

